import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faExternalLinkAlt } from '@/fontawesome';
import type { Event } from '@/models';

interface UpcomingEventsCardProps {
  calendar: Event[];
  className?: string;
}

// Format date for display
const formatEventDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function UpcomingEventsCard({ calendar = [], className = '' }: UpcomingEventsCardProps) {

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 h-[320px] flex flex-col ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-center">
        Kommende Termine
      </h3>

      {/* Events List */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {calendar.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Keine kommenden Termine
            </p>
          </div>
        ) : (
          calendar.map((event) => (
            <a
              key={event.id}
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
            >
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faCalendarDay}
                  className="w-5 h-5 text-blue-600 dark:text-blue-400"
                />
              </div>

              {/* Event Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                    {event.subject}
                  </p>
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    className="w-3 h-3 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {formatEventDate(event.start)}
                </p>
                {event.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                    {event.description}
                  </p>
                )}
              </div>
            </a>
          ))
        )}
      </div>
    </div>
  )
}
